/** Das Bewertungs-Ritual.
 *
 *  Bei fast 300 unbewerteten Büchern ist ein leeres Bewertungsfeld im Detail
 *  wirkungslos — niemand geht 300 Bücher durch. Also legt die App drei zufällige
 *  gelesene Bände vor, ein Regler je Buch, fertig. Kein Zwang: Wegwischen ist
 *  eine gleichwertige Antwort.
 */

import { useCallback, useEffect, useState } from "react";
import type { Book } from "../../../shared/types";
import * as api from "../lib/api";
import { useStore } from "../lib/store";

function Karte({ book, onFertig }: { book: Book; onFertig: () => void }) {
  const { owner, schreibe, ersetze } = useStore();
  const [wert, setWert] = useState(7);
  const bild = api.coverUrl(book.cover_key ?? book.isbn, "s");

  return (
    <article
      className="karte"
      style={{ display: "flex", gap: 18, flexWrap: "wrap" }}
    >
      {bild && (
        <img
          src={bild}
          alt=""
          style={{ width: 92, height: "auto", boxShadow: "var(--sh-near)" }}
          loading="lazy"
        />
      )}
      <div style={{ flex: "1 1 260px" }}>
        <h3
          style={{
            fontFamily: "var(--display)",
            fontWeight: 400,
            fontSize: 20,
            margin: "0 0 2px",
          }}
        >
          {book.title}
        </h3>
        <p className="klein" style={{ marginBottom: 16 }}>
          {book.author}
          {book.read_year ? ` · gelesen ${book.read_year}` : ""}
        </p>

        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <input
            className="regler"
            type="range"
            min={0}
            max={10}
            step={1}
            value={wert}
            style={{ ["--fuell" as string]: `${wert * 10}%` }}
            onChange={(e) => setWert(Number(e.target.value))}
          />
          <strong
            style={{
              fontFamily: "var(--book)",
              fontSize: 24,
              minWidth: 62,
              textAlign: "right",
            }}
          >
            {wert}/10
          </strong>
        </div>

        <div
          style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}
        >
          <button
            className="knopf stark"
            onClick={async () => {
              const neu = await schreibe(() =>
                api.saveBook(owner, book.id, { rating: wert }),
              );
              if (neu) {
                ersetze(neu);
                onFertig();
              }
            }}
          >
            {wert}/10 speichern
          </button>
          <button
            className="knopf"
            onClick={async () => {
              const neu = await schreibe(() =>
                api.saveBook(owner, book.id, { fav: 1, rating: wert }),
              );
              if (neu) {
                ersetze(neu);
                onFertig();
              }
            }}
          >
            ★ und Liebling
          </button>
          <button className="knopf" onClick={onFertig}>
            überspringen
          </button>
        </div>
      </div>
    </article>
  );
}

export function Ritual() {
  const { owner, modus } = useStore();
  const [buecher, setBuecher] = useState<Book[]>([]);
  const [offen, setOffen] = useState(0);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const holen = useCallback(async () => {
    if (modus !== "server") {
      setLaedt(false);
      setFehler(
        "Das Ritual braucht den Server — ohne ihn lässt sich nichts speichern.",
      );
      return;
    }
    setLaedt(true);
    try {
      const res = await api.ritual(owner);
      setBuecher(res.data.books);
      setOffen(res.data.offen);
      setFehler(null);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setLaedt(false);
    }
  }, [owner, modus]);

  useEffect(() => {
    void holen();
  }, [holen]);

  return (
    <>
      <section style={{ maxWidth: "46ch" }}>
        <h2 className="titelzeile">Drei Bücher, drei Regler</h2>
        <p className="lauftext">
          Eine Bewertung ist freiwillig und nie nötig. Aber je mehr Zahlen es
          gibt, desto besser werden die Empfehlungen — also hier drei zufällige
          gelesene Bände, ohne Verpflichtung.
        </p>
        {!fehler && (
          <p className="klein" style={{ marginTop: 16 }}>
            {offen} gelesene Bücher noch ohne Bewertung
          </p>
        )}
      </section>

      {fehler && (
        <div
          className="karte"
          style={{ borderLeft: "3px solid var(--warn)", marginTop: 20 }}
        >
          {fehler}
        </div>
      )}

      <div style={{ display: "grid", gap: 16, marginTop: 34 }}>
        {buecher.map((b) => (
          <Karte
            key={b.id}
            book={b}
            onFertig={() =>
              setBuecher((alle) => alle.filter((x) => x.id !== b.id))
            }
          />
        ))}
      </div>

      {!laedt && !fehler && !buecher.length && (
        <div style={{ marginTop: 30 }}>
          <p className="lauftext">
            {offen
              ? "Diese Runde ist durch."
              : "Alle gelesenen Bücher sind bewertet."}
          </p>
          {offen > 0 && (
            <button className="knopf stark" onClick={() => void holen()}>
              Nächste drei
            </button>
          )}
        </div>
      )}
    </>
  );
}
